"use client"

import { IconButton, Text } from "@/elements"
import React, { FC, useCallback, useEffect, useState } from "react"
import { FragmentType, graphql, useFragment } from "@/gql"
import { ImageSectionItem } from "./ImageSectionItem"
import useEmblaCarousel from "embla-carousel-react"
import clsx from "clsx"
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline"
import { ampli } from "@/ampli"
import type { NovaSession } from "@/types"

const ImageSectionCarouselFragment = graphql(`
  fragment ImageSectionCarousel on CreativeWorkPortfolioProjectConnection {
    edges {
      node {
        __typename
        id
        ...ImageSectionItem
      }
    }
    totalCount
  }
`)

interface Props {
  creativeWorkPortfolioProjects: FragmentType<typeof ImageSectionCarouselFragment> | undefined | null
  imageHeightClass: string
  imageWidthClass: string
  type: "grid" | "list"
  showTextOverlay: boolean
  serverSession: NovaSession | null
  entryPoint: string
}

export const ImageSectionCarousel: FC<Props> = props => {
  const { imageHeightClass, imageWidthClass, type, showTextOverlay, entryPoint } = props
  const isGridType = type === "grid"
  const creativeWorkPortfolioProjects = useFragment(ImageSectionCarouselFragment, props.creativeWorkPortfolioProjects)
  const imageData = creativeWorkPortfolioProjects?.edges
  const additionalProjects = creativeWorkPortfolioProjects?.totalCount
    ? creativeWorkPortfolioProjects?.totalCount - 6
    : 0

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps", // Make sure snaps are properly aligned within the container
  })

  const [canScrollNext, setCanScrollNext] = useState(true)
  const [canScrollPrev, setCanScrollPrev] = useState(false)

  const scrollPrev = useCallback(
    (e: any) => {
      e.preventDefault()
      e.stopPropagation()
      if (emblaApi) emblaApi.scrollPrev()
    },
    [emblaApi]
  )

  const scrollNext = useCallback(
    (e: any) => {
      e.preventDefault()
      e.stopPropagation()
      if (emblaApi) {
        emblaApi.scrollNext()
        ampli.businessCardViewMoreWork({
          entry_point: entryPoint,
        })
      }
    },
    [emblaApi, entryPoint]
  )

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)
  }, [emblaApi, onSelect])

  return (
    <div className={clsx("group relative w-full", isGridType ? "w-[calc(100%+24px)] pl-6" : "w-full")}>
      {canScrollNext && (
        <div
          className={clsx(
            "absolute z-20 mt-auto flex h-full flex-col justify-center",
            isGridType ? "right-3 opacity-0 group-hover:opacity-100" : "right-[-20px]"
          )}>
          <IconButton floating size="medium" onClick={scrollNext} Icon={ArrowRightIcon} data-prevent-nprogress={true} />
        </div>
      )}
      {canScrollPrev && (
        <div
          className={clsx(
            "absolute z-20 mt-auto flex h-full flex-col justify-center",
            isGridType ? "left-3 opacity-0 group-hover:opacity-100" : "left-[-20px]"
          )}>
          <IconButton floating size="medium" onClick={scrollPrev} Icon={ArrowLeftIcon} data-prevent-nprogress={true} />
        </div>
      )}
      <div className="w-full overflow-hidden rounded-l-sm" data-prevent-nprogress={true} ref={emblaRef}>
        <div className="mr-2 flex gap-x-2">
          {imageData?.map((edge, index) => {
            const project = edge?.node
            return (
              <ImageSectionItem
                key={project?.id}
                project={project}
                index={index}
                fourOrLessProjects={false}
                imageHeightClass={imageHeightClass}
                imageWidthClass={imageWidthClass}
                showTextOverlay={showTextOverlay}
              />
            )
          })}

          {additionalProjects > 0 ? (
            <div
              className={clsx(
                "flex shrink-0 flex-col items-center justify-center rounded-sm bg-bg-3",
                imageHeightClass,
                imageWidthClass
              )}>
              <Text variant="CTASmall" className="text-fg-3">
                {`+${additionalProjects}`}
              </Text>
              <Text variant="CTASmall" className="text-fg-3">
                Projects
              </Text>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
